import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  AlertTitle,
  AlertDescription,
  AlertAction,
} from "components/ui/alert";

/**
 * Reusable alert with a title, description, and action button.
 *
 * @param {string}  title        - Alert heading
 * @param {string}  description  - Help text shown below the title
 * @param {string}  actionLabel  - Button text
 * @param {string}  actionTo     - Navigation path
 * @param {string}  [variant]    - "default" | "warning" | "destructive"
 * @param {boolean} [fadeIn]     - Whether to fade in on mount
 */
export default function ActionAlert({
  title,
  description,
  actionLabel,
  actionTo,
  onAction,
  variant = "default",
  fadeIn = false,
  buttonRef,
}) {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(!fadeIn);

  useEffect(() => {
    if (fadeIn) {
      const id = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(id);
    }
  }, [fadeIn]);

  const handleClick = () => {
    if (onAction) return onAction();
    if (actionTo) navigate(actionTo);
  };

  // Reserve action-button gutter ONLY when an action is actually rendered.
  // Without this guard, callers that omit `actionLabel` still get the
  // white `bg-white/90` button rendered with empty text — visible as a
  // small white pill on the upper-right of empty-state alerts (Env tab,
  // Activity tab, etc.).
  const hasAction = !!actionLabel;
  return (
    <Alert
      variant={variant}
      className={`max-w-md ${hasAction ? 'pr-36' : ''} ${fadeIn ? 'transition-opacity duration-300 ease-out' : ''} ${visible ? 'opacity-100' : 'opacity-0'}`}
    >
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{description}</AlertDescription>
      {hasAction && (
        <AlertAction>
          <button
            ref={buttonRef}
            className="rounded-md border border-[rgba(255,255,255,0.2)] bg-white/90 px-3 py-1 text-xs font-medium text-black hover:bg-white transition-colors"
            onClick={handleClick}
          >
            {actionLabel}
          </button>
        </AlertAction>
      )}
    </Alert>
  );
}
